import {
  IVacationAwardImportEntry,
  IVacationAwardImportRow,
  IVacationDate,
  VacationBidRound,
} from "@vmosolutions/oc-interfaces";
import moment from "moment";

import { ICsvFile, ICsvIssue, ICsvParseResult } from "src/utils/csvFile";

/**
 * Reads a finished award file into rows.
 *
 * CargoJet award their bid in their own spreadsheets - one sheet per base and rank - and export
 * them as CSV. Base and rank are not needed here, because a row is matched on employee number, so
 * the sheets may arrive as one file or several.
 *
 * Only the file's own shape is checked here. Whether an employee number names anyone, whether a
 * date range matches a block in the package, and whether the days add up to what a crew member is
 * owed are all decided by the server - the browser's crew list can be stale or filtered, and a
 * judgement made here would not be worth trusting. See `docs/vacationBidding/awardImport.md` in
 * the backend repo.
 */

/** Spellings seen for the employee number column, in the order they are preferred. */
const EMPLOYEE_HEADERS = ["employee", "employeeid", "employeenumber", "empno"];
const FIRST_NAME = "firstname";
const LAST_NAME = "lastname";

/** `R1 Week 3 Start` normalizes to `r1week3start`, which is what this matches. */
const WEEK_COLUMN = /^r(\d+)week(\d+)(start|end)$/;

/**
 * Accepted date spellings.
 *
 * A CSV carries the text a spreadsheet displayed, so the cell format is what reaches us.
 * `04-Jan-2027` is how CargoJet's cells are formatted and `2027-01-04` is what an ISO export gives.
 * Anything else is refused rather than guessed at, because `04/01/2027` is two different days
 * depending on who exported it, and guessing wrong moves somebody's vacation.
 */
const DATE_FORMATS = ["DD-MMM-YYYY", "YYYY-MM-DD"];

const readDate = (value: string | undefined): IVacationDate | null => {
  const text = (value ?? "").trim();
  if (!text) return null;
  const parsed = moment.utc(text, DATE_FORMATS, true);
  if (!parsed.isValid()) return null;
  return { year: parsed.year(), month: parsed.month() + 1, day: parsed.date() };
};

/** Negative when `a` falls before `b`. Compared part by part, because moment reads a date object
 * with a zero-based month and would be a month out. */
const compare = (a: IVacationDate, b: IVacationDate): number => a.year - b.year || a.month - b.month || a.day - b.day;

/** The column holding the employee number, whichever way the export spelled it. */
const employeeHeader = (file: ICsvFile): string | undefined =>
  EMPLOYEE_HEADERS.find((header) => file.headers.includes(header));

interface IWeekColumns {
  round: VacationBidRound;
  week: number;
  start?: string;
  end?: string;
}

/**
 * Finds the awarded-week columns and the round each belongs to.
 *
 * Discovered rather than fixed, because the sheets differ in width and a crew member may hold
 * anywhere from two to eight weeks. The round comes from the column's own label and never from its
 * position: a crew member who did not bid in round 1 has their round 2 award written into the
 * round 1 columns after round 2 is awarded, so position names the wrong round for them.
 */
export const weekColumns = (file: ICsvFile): IWeekColumns[] => {
  const byKey = new Map<string, IWeekColumns>();

  for (const header of file.headers) {
    const match = WEEK_COLUMN.exec(header);
    if (!match) continue;
    const round = Number(match[1]) as VacationBidRound;
    if (round !== VacationBidRound.Round1 && round !== VacationBidRound.Round2) continue;
    const week = Number(match[2]);
    const key = `${round}-${week}`;
    const existing = byKey.get(key) ?? { round, week };
    byKey.set(key, { ...existing, [match[3]]: header });
  }

  return [...byKey.values()].sort((a, b) => a.round - b.round || a.week - b.week);
};

/** `R1 Week 3`, as the file spells it, so a problem can name where to look. */
const label = (column: IWeekColumns): string => `R${column.round} Week ${column.week}`;

export const awardsFromCsv = (file: ICsvFile): ICsvParseResult<IVacationAwardImportRow> => {
  if (file.fatal) return { rows: [], issues: [], fatal: file.fatal };

  const employeeId = employeeHeader(file);
  if (!employeeId) {
    return {
      rows: [],
      issues: [],
      fatal:
        "The file is missing an employee number column. Expected one of " +
        `${EMPLOYEE_HEADERS.join(", ")}. Found: ${file.headers.filter(Boolean).join(", ") || "nothing"}.`,
    };
  }

  const columns = weekColumns(file);
  if (!columns.length) {
    return {
      rows: [],
      issues: [],
      fatal:
        "The file has no awarded-week columns. Expected columns named like " +
        `"R1 Week 1 Start" and "R1 Week 1 End". Found: ${file.headers.filter(Boolean).join(", ") || "nothing"}.`,
    };
  }

  const issues: ICsvIssue[] = [];
  const rows: IVacationAwardImportRow[] = [];
  const lineByEmployeeId = new Map<string, number>();

  for (const row of file.rows) {
    const { line, values } = row;

    const employee = (values[employeeId] ?? "").trim();
    if (!employee) {
      issues.push({ line, message: "No employee number." });
      continue;
    }

    const firstSeen = lineByEmployeeId.get(employee);
    if (firstSeen) {
      issues.push({ line, message: `Employee ${employee} is already on line ${firstSeen}.` });
      continue;
    }

    const entries: IVacationAwardImportEntry[] = [];
    let refused = false;

    for (const column of columns) {
      const startText = column.start ? values[column.start] : undefined;
      const endText = column.end ? values[column.end] : undefined;

      // A week a crew member does not hold is simply blank, which is not a problem.
      if (!(startText ?? "").trim() && !(endText ?? "").trim()) continue;

      const startDate = readDate(startText);
      const endDate = readDate(endText);

      // Half a range cannot be read as a week, and reading it as anything would invent a date, so
      // the whole row is set aside rather than imported short.
      if (!startDate || !endDate) {
        const which = !startDate ? "start" : "end";
        const text = (!startDate ? startText : endText) ?? "";
        issues.push({
          line,
          message:
            `${label(column)} ${which} date "${text}" is not a date. ` +
            "Dates must be written like 04-Jan-2027 or 2027-01-04.",
        });
        refused = true;
        break;
      }

      if (compare(endDate, startDate) < 0) {
        issues.push({ line, message: `${label(column)} ends before it starts.` });
        refused = true;
        break;
      }

      entries.push({ round: column.round, label: label(column), startDate, endDate });
    }

    if (refused) continue;

    if (!entries.length) {
      // Worth saying rather than importing silently: a crew member with no weeks at all is either
      // genuinely unawarded or a sign the file was exported before it was finished.
      issues.push({ line, message: `Employee ${employee} has no awarded weeks.` });
      continue;
    }

    lineByEmployeeId.set(employee, line);
    rows.push({
      employeeId: employee,
      firstName: (values[FIRST_NAME] ?? "").trim() || undefined,
      lastName: (values[LAST_NAME] ?? "").trim() || undefined,
      entries,
    });
  }

  return { rows, issues };
};
